import React, { useState, useEffect } from 'react';
import {
  Box, Container, Heading, Text, VStack, HStack, Badge, Button,
  Alert, AlertIcon, Grid, Image, Flex, useToast,
  Tabs, TabList, TabPanels, Tab, TabPanel, Divider, ButtonGroup
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import Loader from '../components/Loader';
import { fetchOrders, cancelOrder, verifyPayment } from '../services/orderService';

const MotionBox = motion(Box);

const OrdersPage = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingOrderId, setProcessingOrderId] = useState(null);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const data = await fetchOrders();
      setOrders(data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch orders');
      toast({
        title: 'Error',
        description: err.response?.data?.error || 'Failed to fetch orders',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'orange',
      processing: 'blue',
      paid: 'green',
      shipped: 'blue',
      delivered: 'green',
      failed: 'red',
      cancelled: 'gray'
    };
    return colors[status] || 'gray';
  };

  const handleCancelOrder = async (orderId, e) => {
    e.stopPropagation(); // Prevent navigating to order details
    
    if (!window.confirm('Are you sure you want to cancel this order?')) {
      return;
    }

    setProcessingOrderId(orderId);
    try {
      await cancelOrder(orderId);
      toast({
        title: 'Order Cancelled',
        description: 'Your order has been cancelled successfully',
        status: 'success',
        duration: 5000,
        isClosable: true
      });
      // Reload orders to reflect the change
      loadOrders();
    } catch (error) {
      toast({
        title: 'Cancellation Failed',
        description: error.response?.data?.error || 'Failed to cancel order',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setProcessingOrderId(null);
    }
  };

  const handlePayNow = async (order, e) => {
    e.stopPropagation(); // Prevent navigating to order details
    
    setProcessingOrderId(order.orderId);
    
    try {
      // Check if order has razorpayOrderId
      if (!order.razorpayOrderId) {
        throw new Error('Payment session not found. Please contact support.');
      }

      // Use existing Razorpay order ID from the pending order
      const razorpayKeyId = process.env.REACT_APP_RAZORPAY_KEY_ID;

      const options = {
        key: razorpayKeyId,
        amount: order.amount * 100,
        currency: 'INR',
        name: 'Agnir',
        description: `Payment for Order #${order.orderId.substring(0, 13)}`,
        order_id: order.razorpayOrderId, // Use existing Razorpay order ID
        handler: async function (response) {
          try {
            await verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              order_id: order.orderId
            });

            toast({
              title: 'Payment Successful!',
              description: 'Your payment has been processed successfully',
              status: 'success',
              duration: 5000,
              isClosable: true
            });

            // Reload orders and navigate to order details
            loadOrders();
            navigate(`/orders/${order.orderId}`);
          } catch (error) {
            toast({
              title: 'Payment Verification Failed',
              description: error.response?.data?.error || 'Payment could not be verified',
              status: 'error',
              duration: 5000,
              isClosable: true
            });
            setProcessingOrderId(null);
          }
        },
        prefill: {
          email: order.userEmail || '',
          contact: order.userProfile?.phoneNumber || ''
        },
        theme: {
          color: '#000000'
        },
        modal: {
          ondismiss: function() {
            setProcessingOrderId(null);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
      razorpay.on('payment.failed', function (response) {
        toast({
          title: 'Payment Failed',
          description: response.error.description,
          status: 'error',
          duration: 5000,
          isClosable: true
        });
        setProcessingOrderId(null);
      });

    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || error.response?.data?.error || 'Failed to initiate payment',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
      setProcessingOrderId(null);
    }
  };

  // Filter orders by type
  const liveOrders = orders.filter(order =>
    ['pending', 'processing', 'paid', 'shipped'].includes(order.status)
  );

  const pastOrders = orders.filter(order =>
    ['delivered', 'cancelled', 'failed'].includes(order.status)
  );

  const OrderCard = ({ order }) => {
    const isPending = order.status === 'pending';
    
    return (
      <MotionBox
        bg="white"
        p={{ base: 4, md: 6 }}
        borderRadius="xl"
        boxShadow="md"
        cursor="pointer"
        onClick={() => navigate(`/orders/${order.orderId}`)}
        whileHover={{ scale: 1.02, boxShadow: 'lg' }}
        transition={{ duration: 0.2 }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
      {/* Order Header */}
      <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={2}>
        <VStack align="start" spacing={1}>
          <Text fontWeight="700" fontSize={{ base: 'lg', md: 'xl' }} fontFamily="'Cormorant', serif">
            Order #{order.orderId.substring(0, 13)}
          </Text>
          <Text fontSize="sm" color="gray.600" fontFamily="'Zain', sans-serif" fontWeight="500">
            {new Date(order.createdAt).toLocaleDateString('en-IN', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </Text>
        </VStack>
        <Badge
          colorScheme={getStatusColor(order.status)}
          fontSize="sm"
          px={3}
          py={1}
          borderRadius="full"
          textTransform="uppercase"
        >
          {order.status}
        </Badge>
      </Flex>

      {/* Order Items Preview */}
      <VStack spacing={3} align="stretch" mb={4}>
        {order.items.slice(0, 2).map((item) => (
          <Flex key={item.id} gap={3} align="center">
            <Image
              src={item.productImage || '/no_image_available.jpg'}
              alt={item.productName}
              boxSize="60px"
              objectFit="cover"
              borderRadius="md"
            />
            <VStack align="start" flex={1} spacing={0}>
              <Text fontWeight="600" fontSize="sm" noOfLines={1} fontFamily="'Zain', sans-serif">
                {item.productName}
              </Text>
              <Text fontSize="xs" color="gray.600" fontFamily="'Zain', sans-serif" fontWeight="500">
                Qty: {item.quantity} {item.size && `• Size: ${item.size}`}
              </Text>
            </VStack>
            <Text fontWeight="700" fontSize="sm">
              Rs {item.subtotal}
            </Text>
          </Flex>
        ))}
        {order.items.length > 2 && (
          <Text fontSize="xs" color="gray.600" textAlign="center" fontFamily="'Zain', sans-serif" fontWeight="500">
            +{order.items.length - 2} more item{order.items.length - 2 > 1 ? 's' : ''}
          </Text>
        )}
      </VStack>

      <Divider mb={4} />

      {/* Order Footer */}
      <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <VStack align="start" spacing={0}>
          <Text fontSize="sm" color="gray.600" fontFamily="'Zain', sans-serif" fontWeight="500">Total Amount</Text>
          <HStack spacing={2}>
            {(order.couponCode || order.coupon_code) && order.originalAmount && (
              <Text fontSize="sm" color="gray.500" textDecoration="line-through">
                Rs {order.originalAmount}
              </Text>
            )}
            <Text fontWeight="700" fontSize="lg" color={(order.couponCode || order.coupon_code) ? "green.600" : "black"}>
              Rs {order.amount}
            </Text>
          </HStack>
          {(order.couponCode || order.coupon_code) && (
            <Badge colorScheme="green" fontSize="xs" mt={1}>
              🎉 Saved Rs {order.discountAmount || order.discount_amount || 0}
            </Badge>
          )}
        </VStack>
        
        {isPending ? (
          <ButtonGroup size="sm" spacing={2}>
            <Button
              colorScheme="green"
              bg="green.500"
              color="white"
              _hover={{ bg: 'green.600' }}
              onClick={(e) => handlePayNow(order, e)}
              isLoading={processingOrderId === order.orderId}
              loadingText="Processing..."
              fontFamily="'Zain', sans-serif"
              fontWeight="600"
            >
              Pay Now
            </Button>
            <Button
              variant="outline"
              colorScheme="red"
              onClick={(e) => handleCancelOrder(order.orderId, e)}
              isLoading={processingOrderId === order.orderId}
              loadingText="Cancelling..."
              fontFamily="'Zain', sans-serif"
              fontWeight="600"
            >
              Cancel Order
            </Button>
            <Button
            size="sm"
            variant="outline"
            colorScheme="gray"
            borderColor="black"
            color="black"
            _hover={{ bg: 'black', color: 'white' }}
            fontFamily="'Zain', sans-serif"
            fontWeight="600"
          >
            View Details
          </Button>
          </ButtonGroup>
          
        ) : (
          <Button
            size="sm"
            variant="outline"
            colorScheme="gray"
            borderColor="black"
            color="black"
            _hover={{ bg: 'black', color: 'white' }}
            fontFamily="'Zain', sans-serif"
            fontWeight="600"
          >
            View Details
          </Button>
        )}
      </Flex>
    </MotionBox>
  );
};

  const EmptyState = ({ message, icon }) => (
    <Flex
      direction="column"
      align="center"
      justify="center"
      py={16}
      px={4}
    >
      <Text fontSize="6xl" mb={4}>{icon}</Text>
      <Heading as="h3" size="lg" mb={2} color="gray.600" fontFamily="'Cormorant', serif" fontWeight="700">
        {message}
      </Heading>
      <Text color="gray.500" mb={6} textAlign="center" fontFamily="'Zain', sans-serif" fontWeight="500">
        Start shopping to create your first order
      </Text>
      <Button
        bg="black"
        color="white"
        _hover={{ bg: 'gray.800' }}
        onClick={() => navigate('/products/rings')}
        fontFamily="'Zain', sans-serif"
        fontWeight="600"
      >
        Browse Products
      </Button>
    </Flex>
  );

  if (loading) {
    return (
      <Container maxW="container.xl" py={10}>
        <Loader minH="400px" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxW="container.xl" py={10}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
        <Button mt={4} onClick={loadOrders} variant="outline">
          Retry
        </Button>
      </Container>
    );
  }

  return (
    <Box bgImage={`url(${process.env.REACT_APP_BG_URL})`} minH="100vh" py={{ base: 8, md: 12 }}>
      <Container maxW="container.xl">
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <Heading as="h1" size="2xl" mb={8} fontFamily="'Cormorant', serif" fontWeight="700">
            My Orders
          </Heading>

          {orders.length === 0 ? (
            <Box bg="white" borderRadius="xl" boxShadow="md">
              <EmptyState
                message="No Orders Yet"
                icon="📦"
              />
            </Box>
          ) : (
            <Tabs
              variant="unstyled"
              colorScheme="black"
            >
              <TabList
                bg="white"
                borderRadius="xl"
                p={{ base: 1, md: 2 }}
                boxShadow="md"
                mb={6}
              >
                <Tab
                  flex={1}
                  fontWeight="600"
                  fontSize={{ base: 'sm', md: 'md' }}
                  color="gray.700"
                  borderRadius="lg"
                  fontFamily="'Zain', sans-serif"
                  _selected={{
                    bg: 'black',
                    color: 'white',
                    boxShadow: 'md'
                  }}
                  _hover={{
                    bg: 'gray.100',
                    _selected: {
                      bg: 'black',
                      color: 'white'
                    }
                  }}
                  transition="all 0.2s"
                >
                  Live Orders ({liveOrders.length})
                </Tab>
                <Tab
                  flex={1}
                  fontWeight="600"
                  fontSize={{ base: 'sm', md: 'md' }}
                  color="gray.700"
                  borderRadius="lg"
                  fontFamily="'Zain', sans-serif"
                  _selected={{
                    bg: 'black',
                    color: 'white',
                    boxShadow: 'md'
                  }}
                  _hover={{
                    bg: 'gray.100',
                    _selected: {
                      bg: 'black',
                      color: 'white'
                    }
                  }}
                  transition="all 0.2s"
                >
                  Past Orders ({pastOrders.length})
                </Tab>
              </TabList>

              <TabPanels>
                {/* Live Orders Tab */}
                <TabPanel p={0}>
                  {liveOrders.length === 0 ? (
                    <Box bg="white" borderRadius="xl" boxShadow="md">
                      <EmptyState
                        message="No Active Orders"
                        icon="🛍️"
                      />
                    </Box>
                  ) : (
                    <Grid
                      templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
                      gap={6}
                    >
                      {liveOrders.map((order) => (
                        <OrderCard key={order.id} order={order} />
                      ))}
                    </Grid>
                  )}
                </TabPanel>

                {/* Past Orders Tab */}
                <TabPanel p={0}>
                  {pastOrders.length === 0 ? (
                    <Box bg="white" borderRadius="xl" boxShadow="md">
                      <EmptyState
                        message="No Past Orders"
                        icon="📋"
                      />
                    </Box>
                  ) : (
                    <Grid
                      templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
                      gap={6}
                    >
                      {pastOrders.map((order) => (
                        <OrderCard key={order.id} order={order} />
                      ))}
                    </Grid>
                  )}
                </TabPanel>
              </TabPanels>
            </Tabs>
          )}
        </MotionBox>
      </Container>
    </Box>
  );
};

export default OrdersPage;
